import { supabase } from '../../shared/supabase/supabaseClient';
import {
  STORE_REVIEWS_PAGE_SIZE,
  type StoreReviewFilter,
  type StoreReviewRow,
  type StoreReviewStats,
} from '../domain/storeReviewTypes';

const REVIEW_COLUMNS =
  'id, branch_id, customer_id, score, comment, merchant_reply, merchant_replied_at, merchant_replied_by, order_id, appointment_id, created_at';

export async function fetchStoreReviewsForBranch(
  branchId: string,
  filter: StoreReviewFilter = 'all',
  page = 0,
): Promise<StoreReviewRow[]> {
  const from = page * STORE_REVIEWS_PAGE_SIZE;
  const to = from + STORE_REVIEWS_PAGE_SIZE - 1;

  let query = supabase
    .from('store_reviews')
    .select(REVIEW_COLUMNS)
    .eq('branch_id', branchId)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (filter === 'unanswered') {
    query = query.is('merchant_reply', null);
  } else if (filter === 'low') {
    query = query.lte('score', 2);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as StoreReviewRow[];
}

export async function fetchStoreReviewById(reviewId: string): Promise<StoreReviewRow | null> {
  const { data, error } = await supabase
    .from('store_reviews')
    .select(REVIEW_COLUMNS)
    .eq('id', reviewId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as StoreReviewRow | null) ?? null;
}

export async function applyStoreReviewReply(reviewId: string, reply: string): Promise<void> {
  const { error } = await supabase.rpc('apply_store_review_reply', {
    p_review_id: reviewId,
    p_reply: reply,
  });
  if (error) {
    const msg = error.message || '';
    if (msg.includes('reply_required')) throw new Error('Escribí una respuesta.');
    if (msg.includes('reply_too_long')) throw new Error('Máximo 500 caracteres.');
    if (msg.includes('module_permission_denied')) throw new Error('No tenés permiso de responder.');
    if (msg.includes('branch_access_denied') || msg.includes('not_authenticated')) {
      throw new Error('No podés responder.');
    }
    if (msg.includes('review_not_found')) throw new Error('No encontramos esa reseña.');
    throw new Error(msg || 'No se pudo publicar la respuesta.');
  }
}

export async function fetchStoreReviewStats(branchId: string): Promise<StoreReviewStats> {
  const { data, error } = await supabase.rpc('get_store_review_stats', {
    p_branch_id: branchId,
  });
  if (error) throw new Error(error.message);
  return (data as StoreReviewStats) ?? { total: 0, unanswered: 0, low: 0, avg_score: 0 };
}
